/**
 * TNCY-5 — TanStack Query hooks for the Partner Companies page.
 *
 * Wire (tenancy/v1@1.1.0):
 *   GET    /api/tenancy/v1/links                       → listLinks (active + suspended)
 *   GET    /api/tenancy/v1/invitations                 → listInvitations (metadata-only, TNCY-10)
 *   POST   /api/tenancy/v1/invitations                 → createInvitation (returns code)
 *   DELETE /api/tenancy/v1/invitations/{id}            → revokeInvitation
 *   POST   /api/tenancy/v1/invitations/{code}/redeem   → redeemInvitation
 *   PATCH  /api/tenancy/v1/links/{tenantId}            → suspendLink ({suspended:true})
 *
 * Reuses the authFetch + react-query + optimistic pattern from the admin
 * surface (`features/admin/queries/hooks.ts`). TNCY-10 added
 * `GET /invitations` (metadata-only — never re-exposes the one-time `code`),
 * so the outstanding list is now query-backed and survives a refresh; the
 * code is shown once at creation only (see PartnerCompaniesPage).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@aeriosiq/auth-client";
import { tenancyFetch } from "./tenancyFetch.js";
import { tenancyKeys } from "./keys.js";
import type {
  CreateInvitationResponse,
  InvitationsListResponse,
  LinksResponse,
  RedeemInvitationResponse,
  TenantLink,
  TenantSettings,
} from "./types.js";

/** GET /links — ALL the caller's links, active and suspended (XT-D26). */
export function useLinksQuery() {
  // Gate on the resolved tenant. On a deep-link / fresh-page boot this query
  // can fire BEFORE tenant-from-host hydrates the auth store, so tenancyFetch
  // sends no X-Tenant-ID and the gateway 400s ("X-Tenant-ID header is
  // required"). enabled-gating defers the fetch until the slug is present.
  // Same boot-race fix as notifications/dashboard (2026-06-04).
  const tenantReady = useAuthStore((s) => s.tenant != null);
  return useQuery<LinksResponse>({
    queryKey: tenancyKeys.links(),
    queryFn: () => tenancyFetch<LinksResponse>("/links"),
    enabled: tenantReady,
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * GET /invitations — the caller's still-**outstanding** invitations
 * (TNCY-10). Metadata-only: the response carries no `code`, so the list
 * survives a refresh without ever re-exposing the single-use token. Default
 * (no `?status`) returns outstanding only, ordered newest-first.
 */
export function useInvitationsQuery() {
  // Same tenant-readiness gate as useLinksQuery (boot-race 400s).
  const tenantReady = useAuthStore((s) => s.tenant != null);
  return useQuery<InvitationsListResponse>({
    queryKey: tenancyKeys.invitations(),
    queryFn: () => tenancyFetch<InvitationsListResponse>("/invitations"),
    enabled: tenantReady,
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * POST /invitations — issue a single-use, 7-day code to share out-of-band.
 * The returned `code` is shown once (caller's responsibility); on success we
 * invalidate the invitations list so the new outstanding row appears.
 */
export function useCreateInvitationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      tenancyFetch<CreateInvitationResponse>("/invitations", {
        method: "POST",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tenancyKeys.invitations() });
    },
  });
}

/**
 * DELETE /invitations/{id} — revoke an unredeemed invitation (XT-D16).
 * Optimistic: drop the row from the cached outstanding list immediately,
 * rolling back on error, then re-sync.
 */
export function useRevokeInvitationMutation() {
  const qc = useQueryClient();
  return useMutation<
    undefined,
    Error,
    string,
    { previous: InvitationsListResponse | undefined }
  >({
    mutationFn: (id: string) =>
      tenancyFetch<undefined>(`/invitations/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: tenancyKeys.invitations() });
      const previous = qc.getQueryData<InvitationsListResponse>(
        tenancyKeys.invitations(),
      );
      if (previous) {
        qc.setQueryData<InvitationsListResponse>(tenancyKeys.invitations(), {
          ...previous,
          items: previous.items.filter((i) => i.invitationId !== id),
        });
      }
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData<InvitationsListResponse>(
          tenancyKeys.invitations(),
          ctx.previous,
        );
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: tenancyKeys.invitations() });
    },
  });
}

/**
 * POST /invitations/{code}/redeem — redeem a code received out-of-band.
 * Outcome is `created` | `reactivated` | `noop` (XT-D26). Invalidates the
 * links list so the new/reactivated partner appears immediately.
 */
export function useRedeemInvitationMutation() {
  const qc = useQueryClient();
  return useMutation<RedeemInvitationResponse, Error, string>({
    mutationFn: (code: string) =>
      tenancyFetch<RedeemInvitationResponse>(
        `/invitations/${encodeURIComponent(code)}/redeem`,
        { method: "POST" },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tenancyKeys.links() });
    },
  });
}

/**
 * GET /settings — the caller tenant's admin-policy settings (PM-D39).
 * Currently `{ maxAiModelsPerProject: number | null }` — the cap on AI
 * models a project may select (`null` = unlimited; default 3). Readable by
 * project_manager + tenant_admin (used to cap the AiModelMultiSelect in the
 * create wizard + edit panel, and read/written on the Administration field).
 * Cached a few minutes — the limit changes rarely.
 */
export function useTenantSettingsQuery() {
  // Same tenant-readiness gate as the other tenancy queries (boot-race 400s).
  const tenantReady = useAuthStore((s) => s.tenant != null);
  return useQuery<TenantSettings>({
    queryKey: tenancyKeys.settings(),
    queryFn: () => tenancyFetch<TenantSettings>("/settings"),
    enabled: tenantReady,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * PUT /settings — set the caller tenant's admin-policy settings (PM-D39).
 * tenant_admin only (the BE re-asserts; the FE gates the page). The body is
 * the full `TenantSettings` shape (`maxAiModelsPerProject`: int ≥ 1 or null).
 * Invalidates the settings query so the selector cap + the field re-read.
 */
export function useUpdateTenantSettingsMutation() {
  const qc = useQueryClient();
  return useMutation<TenantSettings, Error, TenantSettings>({
    mutationFn: (next: TenantSettings) =>
      tenancyFetch<TenantSettings>("/settings", {
        method: "PUT",
        body: next,
      }),
    onSuccess: (updated) => {
      // Write the canonical response into the cache, then invalidate so any
      // dependent reader (the selector cap) re-syncs.
      qc.setQueryData<TenantSettings>(tenancyKeys.settings(), updated);
      void qc.invalidateQueries({ queryKey: tenancyKeys.settings() });
    },
  });
}

/**
 * PATCH /links/{tenantId} — suspend a link (XT-D18/D26). Suspend only;
 * there is no unilateral resume. Optimistic: flip the row to suspended in
 * the cached links list so the table updates instantly, rolling back on
 * error.
 */
export function useSuspendLinkMutation() {
  const qc = useQueryClient();
  return useMutation<
    TenantLink,
    Error,
    string,
    { previous: LinksResponse | undefined }
  >({
    mutationFn: (partnerTenantId: string) =>
      tenancyFetch<TenantLink>(
        `/links/${encodeURIComponent(partnerTenantId)}`,
        { method: "PATCH", body: { suspended: true } },
      ),
    onMutate: async (partnerTenantId) => {
      await qc.cancelQueries({ queryKey: tenancyKeys.links() });
      const previous = qc.getQueryData<LinksResponse>(tenancyKeys.links());
      if (previous) {
        qc.setQueryData<LinksResponse>(tenancyKeys.links(), {
          ...previous,
          items: previous.items.map((l) =>
            l.partnerTenantId === partnerTenantId
              ? {
                  ...l,
                  status: "suspended",
                  suspendedAt: new Date().toISOString(),
                }
              : l,
          ),
        });
      }
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData<LinksResponse>(tenancyKeys.links(), ctx.previous);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tenancyKeys.links() });
    },
  });
}
